import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { LayoutDashboard, Sparkles, LogOut, Loader2, Plus, Pencil, Trash2, CalendarCheck, BadgePercent, Settings, RefreshCw, Ticket, Image as ImageIcon, Star, Disc3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BookingsTab } from "@/components/admin/BookingsTab";
import { SpecialsTab } from "@/components/admin/SpecialsTab";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { OffersTab } from "@/components/admin/OffersTab";
import { GalleryTab } from "@/components/admin/GalleryTab";
import { ReviewsTab } from "@/components/admin/ReviewsTab";
import { ImageUploadField } from "@/components/admin/ImageUploadField";
import { SpinMap } from "@/components/admin/SpinMap";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "texoma_admin_token";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const emptyService = { name: "", description: "", price: "", duration: "", category: "adventure", image: "", active: true, pricing_mode: "per_group", group_price: "", max_guests: "", child_price: "" };

const AdminLogin = ({ onLogin }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Login failed");
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem("texoma_admin_role", data.role || "owner");
      onLogin(data.role || "owner");
    } catch {
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm border border-border/60 rounded-xl bg-white p-8 space-y-4" data-testid="admin-login-form">
        <h1 className="font-heading text-2xl font-semibold text-foreground">Admin Login</h1>
        <Input data-testid="admin-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="font-body" />
        <Input data-testid="admin-password" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="font-body" />
        {error && <p data-testid="admin-login-error" className="font-body text-sm text-destructive">{error}</p>}
        <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white font-body rounded-full h-11">
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
        </Button>
      </form>
    </div>
  );
};

const ServiceDialog = ({ open, onOpenChange, service, onSaved }) => {
  const [form, setForm] = useState(emptyService);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(service ? { ...service } : emptyService);
  }, [service, open]);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const save = async () => {
    if (!form.name.trim() || form.price === "" || Number(form.price) < 0) {
      toast.error("Name and a valid price are required");
      return;
    }
    if (form.pricing_mode === "both" && (!form.group_price || Number(form.group_price) <= 0)) {
      toast.error("A group rate is required when offering both pricing options");
      return;
    }
    setSaving(true);
    try {
      const url = service ? `${API}/api/admin/services/${service.id}` : `${API}/api/admin/services`;
      const res = await fetch(url, {
        method: service ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          ...form,
          price: Number(form.price),
          group_price: Number(form.group_price) || 0,
          max_guests: Number(form.max_guests) || 0,
          child_price: Number(form.child_price) || 0,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.detail || "Could not save service");
        return;
      }
      toast.success(service ? "Service updated" : "Service added");
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Could not save service");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="service-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{service ? "Edit Service" : "Add Service"}</DialogTitle>
          <DialogDescription className="font-body text-xs">
            Changes go live on the website immediately after saving.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="font-body text-sm">Name *</Label>
            <Input data-testid="service-name" value={form.name} onChange={update("name")} className="font-body" />
          </div>
          <div>
            <Label className="font-body text-sm">Description</Label>
            <Textarea data-testid="service-description" value={form.description} onChange={update("description")} rows={2} className="font-body" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-body text-sm">{["per_person", "both"].includes(form.pricing_mode) ? "Per-Person Price (USD) *" : "Price (USD) *"}</Label>
              <Input data-testid="service-price" type="number" min="0" step="0.01" value={form.price} onChange={update("price")} className="font-body" />
            </div>
            <div>
              <Label className="font-body text-sm">Duration</Label>
              <Input data-testid="service-duration" value={form.duration} onChange={update("duration")} className="font-body" />
            </div>
          </div>
          <div>
            <Label className="font-body text-sm">Pricing Mode</Label>
            <select data-testid="service-pricing-mode" value={form.pricing_mode || "per_group"} onChange={update("pricing_mode")} className="w-full h-10 rounded-md border border-input bg-background px-3 font-body text-sm">
              <option value="per_group">Flat / group rate (one price covers the group)</option>
              <option value="per_person">Per person (price × number of guests)</option>
              <option value="both">Both (guest chooses per person or group rate)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {form.pricing_mode === "both" && (
              <div>
                <Label className="font-body text-sm">Group Rate (USD) *</Label>
                <Input data-testid="service-group-price" type="number" min="0" step="0.01" value={form.group_price} onChange={update("group_price")} className="font-body" />
              </div>
            )}
            {["per_person", "both"].includes(form.pricing_mode) && (
              <div>
                <Label className="font-body text-sm">Child Price (USD, optional)</Label>
                <Input data-testid="service-child-price" type="number" min="0" step="0.01" placeholder="0 = same as adult" value={form.child_price} onChange={update("child_price")} className="font-body" />
              </div>
            )}
            <div>
              <Label className="font-body text-sm">Max Guests</Label>
              <Input data-testid="service-max-guests" type="number" min="0" step="1" placeholder="0 = no limit shown" value={form.max_guests} onChange={update("max_guests")} className="font-body" />
            </div>
          </div>
          <div>
            <Label className="font-body text-sm">Category</Label>
            <select data-testid="service-category" value={form.category} onChange={update("category")} className="w-full h-10 rounded-md border border-input bg-background px-3 font-body text-sm">
              <option value="adventure">Adventure</option>
              <option value="romantic">Romantic</option>
              <option value="nature">Nature</option>
              <option value="entertainment">Entertainment</option>
            </select>
          </div>
          <ImageUploadField value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testIdPrefix="service-image" />
          <div className="flex items-center gap-2">
            <Switch data-testid="service-active" checked={Boolean(form.active)} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <span className="font-body text-sm text-foreground">Active (visible & bookable on the site)</span>
          </div>
          <Button data-testid="service-save" onClick={save} disabled={saving} className="w-full bg-primary hover:bg-primary/90 text-white font-body rounded-full">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ServicesTab = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/services`, { headers: authHeaders() });
      if (res.ok) setServices(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const remove = async (id) => {
    if (!window.confirm("Delete this service? Guests will no longer be able to book it.")) return;
    const res = await fetch(`${API}/api/admin/services/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) {
      toast.success("Service deleted");
      fetchServices();
    } else {
      toast.error("Could not delete service");
    }
  };

  const toggleActive = async (svc) => {
    const res = await fetch(`${API}/api/admin/services/${svc.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ ...svc, active: !svc.active }),
    });
    if (res.ok) {
      fetchServices();
    } else {
      toast.error("Could not update service");
    }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div data-testid="admin-services-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="font-body text-sm text-muted-foreground">{services.length} services · changes go live instantly</p>
        <Button data-testid="add-service-button" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-accent hover:bg-accent/90 text-white font-body rounded-full">
          <Plus size={16} className="mr-1" /> Add Service
        </Button>
      </div>
      <div className="space-y-2">
        {services.map((svc) => (
          <div key={svc.id} data-testid={`service-row-${svc.id}`} className={`flex items-center gap-4 rounded-lg border p-4 ${svc.active ? "border-border/60 bg-white" : "border-border/40 bg-muted/40 opacity-70"}`}>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-base font-semibold text-foreground">{svc.name}</p>
              <p className="font-body text-xs text-muted-foreground truncate">
                {svc.category} · {svc.duration || "—"} · {svc.pricing_mode === "per_person" ? "per person" : svc.pricing_mode === "both" ? "per person / group" : "flat rate"}{Number(svc.max_guests) > 0 ? ` · max ${svc.max_guests}` : ""}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <span className="font-heading text-base font-semibold text-primary">${Number(svc.price).toFixed(2)}{svc.pricing_mode === "per_person" || svc.pricing_mode === "both" ? <span className="font-body text-xs text-muted-foreground font-normal">/person</span> : null}</span>
              {svc.pricing_mode === "both" && Number(svc.group_price) > 0 && (
                <p className="font-body text-xs text-muted-foreground">${Number(svc.group_price).toFixed(2)} group</p>
              )}
              {["per_person", "both"].includes(svc.pricing_mode) && Number(svc.child_price) > 0 && (
                <p className="font-body text-xs text-muted-foreground">${Number(svc.child_price).toFixed(2)} child</p>
              )}
            </div>
            <Switch data-testid={`service-toggle-${svc.id}`} checked={Boolean(svc.active)} onCheckedChange={() => toggleActive(svc)} />
            <button data-testid={`service-edit-${svc.id}`} onClick={() => { setEditing(svc); setDialogOpen(true); }} className="text-muted-foreground hover:text-primary p-1" aria-label="Edit">
              <Pencil size={16} />
            </button>
            <button data-testid={`service-delete-${svc.id}`} onClick={() => remove(svc.id)} className="text-muted-foreground hover:text-destructive p-1" aria-label="Delete">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <ServiceDialog open={dialogOpen} onOpenChange={setDialogOpen} service={editing} onSaved={fetchServices} />
    </div>
  );
};

const fmtDate = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-xl border border-border/60 bg-white p-5" data-testid={testId}>
    <p className="font-body text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-heading text-2xl font-semibold text-foreground mt-1">{value}</p>
  </div>
);

const DashboardTab = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const syncListings = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/guesty/listings?refresh=true`);
      if (!res.ok) throw new Error();
      const d = await res.json();
      toast.success(`Synced ${d.listings.length} properties from Guesty`, { description: "Property names, photos, prices, and guest counts are now up to date." });
    } catch {
      toast.error("Could not sync from Guesty. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/api/admin/dashboard`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <p data-testid="dashboard-error" className="font-body text-sm text-muted-foreground py-8">
        Could not load the dashboard. Please refresh the page or sign in again.
      </p>
    );
  }
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div data-testid="admin-dashboard-tab" className="space-y-8">
      <div className="flex justify-end">
        <Button data-testid="sync-guesty-button" variant="outline" size="sm" onClick={syncListings} disabled={syncing} className="font-body rounded-full">
          {syncing ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />} Sync Properties from Guesty
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Revenue" value={`$${data.stats.revenue.toFixed(2)}`} testId="stat-revenue" />
        <StatCard label="Paid Bookings" value={data.stats.paid_bookings} testId="stat-bookings" />
        <StatCard label="Cart Orders" value={data.stats.paid_orders} testId="stat-orders" />
        <StatCard label="Upcoming Arrivals" value={data.stats.upcoming_arrivals} testId="stat-arrivals" />
      </div>

      {data.wheel_stats && (
        <div data-testid="wheel-stats-panel" className="rounded-xl border border-border/60 bg-white p-5">
          <div className="flex items-center gap-2 mb-4">
            <Disc3 size={17} className="text-accent" />
            <h3 className="font-heading text-lg font-semibold text-foreground">Prize Wheel</h3>
            <span data-testid="wheel-stats-total" className="ml-auto font-body text-sm text-muted-foreground">
              {data.wheel_stats.total} total spins
            </span>
          </div>
          {data.wheel_stats.prizes.length === 0 ? (
            <p className="font-body text-sm text-muted-foreground">No spins yet — the wheel appears to new visitors on the homepage.</p>
          ) : (
            <div className="space-y-2">
              {data.wheel_stats.prizes.map((p) => {
                const pct = data.wheel_stats.total ? Math.round((p.count / data.wheel_stats.total) * 100) : 0;
                return (
                  <div key={p.code} data-testid={`wheel-stat-${p.code}`} className="flex items-center gap-3">
                    <span className="font-heading text-xs font-semibold text-primary tracking-wider w-32 shrink-0">{p.code}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-accent" style={{ width: `${Math.max(pct, 3)}%` }} />
                    </div>
                    <span className="font-body text-xs text-muted-foreground w-20 text-right">{p.count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          )}
          {data.wheel_stats.spinners > 0 && (
            <>
              <div className="grid grid-cols-3 gap-3 mt-5" data-testid="wheel-conversion-stats">
                <div className="rounded-lg bg-muted/60 p-3 text-center">
                  <p className="font-heading text-xl font-semibold text-foreground">{data.wheel_stats.spinners}</p>
                  <p className="font-body text-xs text-muted-foreground">Spinners</p>
                </div>
                <div className="rounded-lg bg-muted/60 p-3 text-center">
                  <p data-testid="wheel-conversion-rate" className="font-heading text-xl font-semibold text-foreground">{data.wheel_stats.converted} <span className="text-sm text-accent">({data.wheel_stats.conversion_rate}%)</span></p>
                  <p className="font-body text-xs text-muted-foreground">Became Paying Guests</p>
                </div>
                <div className="rounded-lg bg-muted/60 p-3 text-center">
                  <p className="font-heading text-xl font-semibold text-primary">${(data.wheel_stats.converted_revenue || 0).toFixed(2)}</p>
                  <p className="font-body text-xs text-muted-foreground">Revenue from Spinners</p>
                </div>
              </div>
              <div className="mt-4" data-testid="recent-spins-list">
                <p className="font-body text-xs uppercase tracking-wider text-muted-foreground mb-2">Recent Spins</p>
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {(data.wheel_stats.recent_spins || []).map((s) => (
                    <div key={s.email} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 rounded-md border border-border/50 px-3 py-2 font-body text-xs">
                      <span className="font-medium text-foreground">{s.email}</span>
                      <span className="text-primary font-semibold tracking-wider">{s.code}</span>
                      {s.location && <span className="text-muted-foreground">📍 {s.location}</span>}
                      {s.device && <span className="text-muted-foreground">{s.device}</span>}
                      {s.converted && <span className="ml-auto rounded-full bg-accent/10 text-accent px-2 py-0.5 text-[10px] uppercase tracking-wider">Booked</span>}
                      <span className={`text-muted-foreground ${s.converted ? "" : "ml-auto"}`}>{fmtDate(s.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
              {(data.wheel_stats.map_points || []).length > 0 && (
                <div className="mt-4">
                  <p className="font-body text-xs uppercase tracking-wider text-muted-foreground mb-2">Spin Locations — Marketing Reach</p>
                  <SpinMap points={data.wheel_stats.map_points} />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div>
        <h3 className="font-heading text-lg font-semibold text-foreground mb-3">Upcoming Arrivals (synced from Guesty)</h3>
        {data.arrivals.length === 0 ? (
          <p className="font-body text-sm text-muted-foreground">No upcoming arrivals yet.</p>
        ) : (
          <div className="space-y-2">
            {data.arrivals.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-white p-4 font-body text-sm">
                <span className="font-medium text-foreground">{a.guest_name || "Guest"}</span>
                <span className="text-muted-foreground">{fmtDate(a.check_in)} → {fmtDate(a.check_out)}</span>
                <span className="text-muted-foreground">{a.source || ""}</span>
                <span className="font-semibold text-primary">${a.total ?? "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold text-foreground mb-3">Recent Website Bookings</h3>
        {data.bookings.length === 0 ? (
          <p className="font-body text-sm text-muted-foreground">No paid bookings yet.</p>
        ) : (
          <div className="space-y-2">
            {data.bookings.map((b) => (
              <div key={b.id} className="rounded-lg border border-border/60 bg-white p-4 font-body text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{b.first_name} {b.last_name} · {b.listing_title || b.listing_id}</span>
                  <span className="text-muted-foreground">{fmtDate(b.check_in)} → {fmtDate(b.check_out)} · {b.guests_count} guests</span>
                  <span className="font-semibold text-primary">${b.amount?.toFixed(2)}</span>
                </div>
                {b.addons?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">Add-ons: {b.addons.map((a) => a.name).join(", ")}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold text-foreground mb-3">Recent Cart Orders</h3>
        {data.orders.length === 0 ? (
          <p className="font-body text-sm text-muted-foreground">No cart orders yet.</p>
        ) : (
          <div className="space-y-2">
            {data.orders.map((o) => (
              <div key={o.id} className="rounded-lg border border-border/60 bg-white p-4 font-body text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{o.first_name} {o.last_name}</span>
                  <span className="text-muted-foreground">{o.experiences?.map((e) => `${e.name} ×${e.qty}`).join(", ") || "Stay only"}</span>
                  <span className="font-semibold text-primary">${o.amount?.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [role, setRole] = useState("owner");
  const [checking, setChecking] = useState(true);
  const isOwner = role === "owner" || role === "admin";

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        setRole(d.role || "owner");
        setAuthed(true);
      })
      .catch(() => setAuthed(false))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("texoma_admin_role");
    setAuthed(false);
  };

  return (
    <>
      <Helmet>
        <title>Admin | Texoma Resorts</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <section className="pt-28 pb-24 md:pt-32 min-h-[80vh]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {checking ? (
            <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>
          ) : !authed ? (
            <AdminLogin onLogin={(r) => { setRole(r); setAuthed(true); }} />
          ) : (
            <div data-testid="admin-panel">
              <div className="flex items-center justify-between mb-8">
                <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">
                  {isOwner ? "Owner Admin" : "Staff Admin"}
                </h1>
                <Button data-testid="admin-logout" variant="outline" onClick={logout} className="font-body rounded-full text-sm">
                  <LogOut size={14} className="mr-1" /> Sign Out
                </Button>
              </div>
              <Tabs defaultValue="dashboard">
                <TabsList className="mb-6 flex-wrap h-auto">
                  <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="font-body">
                    <LayoutDashboard size={15} className="mr-1" /> Dashboard
                  </TabsTrigger>
                  <TabsTrigger value="bookings" data-testid="tab-bookings" className="font-body">
                    <CalendarCheck size={15} className="mr-1" /> Bookings
                  </TabsTrigger>
                  <TabsTrigger value="services" data-testid="tab-services" className="font-body">
                    <Sparkles size={15} className="mr-1" /> Services
                  </TabsTrigger>
                  <TabsTrigger value="specials" data-testid="tab-specials" className="font-body">
                    <BadgePercent size={15} className="mr-1" /> Specials
                  </TabsTrigger>
                  <TabsTrigger value="offers" data-testid="tab-offers" className="font-body">
                    <Ticket size={15} className="mr-1" /> Offers
                  </TabsTrigger>
                  <TabsTrigger value="gallery" data-testid="tab-gallery" className="font-body">
                    <ImageIcon size={15} className="mr-1" /> Gallery
                  </TabsTrigger>
                  <TabsTrigger value="reviews" data-testid="tab-reviews" className="font-body">
                    <Star size={15} className="mr-1" /> Reviews
                  </TabsTrigger>
                  {isOwner && (
                    <TabsTrigger value="settings" data-testid="tab-settings" className="font-body">
                      <Settings size={15} className="mr-1" /> Settings
                    </TabsTrigger>
                  )}
                </TabsList>
                <TabsContent value="dashboard"><DashboardTab /></TabsContent>
                <TabsContent value="bookings"><BookingsTab /></TabsContent>
                <TabsContent value="services"><ServicesTab /></TabsContent>
                <TabsContent value="specials"><SpecialsTab /></TabsContent>
                <TabsContent value="offers"><OffersTab /></TabsContent>
                <TabsContent value="gallery"><GalleryTab /></TabsContent>
                <TabsContent value="reviews"><ReviewsTab /></TabsContent>
                {isOwner && <TabsContent value="settings"><SettingsTab /></TabsContent>}
              </Tabs>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
